import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface UserCompany {
  company_id: string;
  company_name: string;
  role_name: string;
  profile_id: string;
}

export const useCompanySelector = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const companiesQuery = useQuery({
    queryKey: ["user-companies", user?.id],
    queryFn: async (): Promise<UserCompany[]> => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from("user_profiles")
        .select("id, company_id, company:companies(name), role:roles(name)")
        .eq("user_id", user.id)
        .eq("status", "active");
      if (error) throw error;
      return (data ?? []).map((p: any) => ({
        company_id: p.company_id,
        company_name: p.company?.name ?? "Unknown",
        role_name: p.role?.name ?? "user",
        profile_id: p.id,
      }));
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  const activeCompanyQuery = useQuery({
    queryKey: ["active-company", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from("user_active_company")
        .select("company_id")
        .eq("user_id", user.id)
        .maybeSingle();
      if (error) throw error;
      return data?.company_id ?? null;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  const setActiveCompany = useMutation({
    mutationFn: async (companyId: string) => {
      const { error } = await supabase.rpc("set_active_company", {
        _company_id: companyId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["active-company"] });
      queryClient.invalidateQueries({ queryKey: ["user-permissions"] });
      queryClient.invalidateQueries();
    },
  });

  const companies = companiesQuery.data ?? [];
  const hasMultipleCompanies = companies.length > 1;
  const activeCompanyId = activeCompanyQuery.data ?? companies[0]?.company_id ?? null;
  const needsSelection = hasMultipleCompanies && !activeCompanyId;

  return {
    companies,
    hasMultipleCompanies,
    activeCompanyId,
    needsSelection,
    setActiveCompany,
    isLoading: companiesQuery.isLoading || activeCompanyQuery.isLoading,
  };
};
